import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Spin, Alert } from 'antd';
import { CardPaymentForm } from './CardPaymentForm';
import { withDevModalFilePath } from '../common';
import { trpc } from '../../lib/trpc';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = loadStripe(stripePublishableKey);
const { Modal } = withDevModalFilePath('client/src/components/subscription/SubscriptionCardPaymentModal.tsx');

export interface SubscriptionCardPaymentModalProps {
  open: boolean;
  planCode: string;
  planName: string;
  amount: number;  // 税抜金額（フォールバック用）
  prorationAmount?: number;  // クライアント計算の日割り額（フォールバック用）
  predictionAddonQuantity?: number;
  userAddonQuantity?: number;
  agentCode?: string;
  promotionCode?: string;
  promotionCodeValidated?: boolean;  // 検証済みフラグ
  promotionCodeValid?: boolean;      // 検証結果（有効/無効）
  promotionCodeDiscount?: {          // 割引情報
    percentOff: number | null;
    amountOff: number | null;
    currency: string | null;
    duration: string;
    durationInMonths: number | null;
  } | null;
  customerEmail?: string;  // 請求先メールアドレス
  onSuccess: (subscriptionId: string) => void;
  onCancel: () => void;
}

export function SubscriptionCardPaymentModal({
  open,
  planCode,
  planName,
  amount,
  prorationAmount,
  predictionAddonQuantity,
  userAddonQuantity,
  agentCode,
  promotionCode,
  promotionCodeValidated,
  promotionCodeValid,
  promotionCodeDiscount,
  customerEmail,
  onSuccess,
  onCancel,
}: SubscriptionCardPaymentModalProps) {
  // Stripeプレビューの状態管理
  const [stripePreview, setStripePreview] = useState<{
    customerId: string;
    subtotal: number;
    tax: number;
    total: number;
    discountAmount: number;
  } | null>(null);

  // プレビュー取得API
  const previewQuery = trpc.stripe.getSubscriptionPreviewWithCustomer.useQuery(
    {
      planCode: planCode as ManagedPlanCode,
      predictionAddonQuantity,
      userAddonQuantity,
      promotionCode: promotionCodeValid && promotionCodeValidated ? promotionCode : undefined,
    },
    {
      enabled: open,  // モーダルが開いている時のみ実行
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,  // 30秒間キャッシュ
    }
  );

  // プレビュー結果を状態に保存
  useEffect(() => {
    if (previewQuery.data) {
      setStripePreview({
        customerId: previewQuery.data.customerId,
        subtotal: previewQuery.data.preview.subtotal,
        tax: previewQuery.data.preview.tax,
        total: previewQuery.data.preview.total,
        discountAmount: previewQuery.data.preview.discountAmount,
      });
    }
  }, [previewQuery.data]);

  // モーダルが閉じたらプレビューをリセット
  useEffect(() => {
    if (!open) {
      setStripePreview(null);
    }
  }, [open]);

  const isLoading = previewQuery.isLoading;
  const isError = previewQuery.isError;

  return (
    <Modal
      title="クレジットカードで契約"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnHidden
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#666' }}>請求金額を計算中...</p>
        </div>
      ) : isError ? (
        <Alert
          type="error"
          message="プレビューの取得に失敗しました"
          description="ページを再読み込みして再度お試しください。"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {!isLoading && (
        <Elements stripe={stripePromise}>
          <CardPaymentForm
            planCode={planCode}
            planName={planName}
            amount={amount}
            prorationAmount={prorationAmount}
            predictionAddonQuantity={predictionAddonQuantity}
            userAddonQuantity={userAddonQuantity}
            agentCode={agentCode}
            promotionCode={promotionCode}
            promotionCodeValidated={promotionCodeValidated}
            promotionCodeValid={promotionCodeValid}
            promotionCodeDiscount={promotionCodeDiscount}
            customerEmail={customerEmail}
            onSuccess={onSuccess}
            onCancel={onCancel}
            // Stripeプレビューデータを渡す
            stripePreview={stripePreview}
          />
        </Elements>
      )}
    </Modal>
  );
}
